import { BoundingBox, ChatCircleText, FileText } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Flows } from '../../../../util/Types/Folder';
import { AutoTriggerBadge } from '../../AutoTriggerBadge';
import { DraggableItemShell } from '../dnd/DraggableItemShell';
import type { ReservedFolderType } from '../dnd/types';
import { isFluxbotFlow, isFormFlow } from '../utils';
import { ActionMenu } from './ActionMenu';
import { Item } from './Item';
import { useAgentsSidebarContext } from './context';
import { useSidebarItemDraggable } from './useSidebarItemDraggable';

interface AgentItemProps {
  flow: Flows;
  folderId: string;
  flowFolderName?: string;
  forceFluxbotIcon?: boolean;
  isNested?: boolean;
  spaceId?: string | null;
  parentId?: string | null;
  ancestorReservedType?: ReservedFolderType | null;
  onRename?: (flow: Flows, folderId: string) => void;
  onDelete?: (flow: Flows) => void;
  onShare?: (flow: Flows, folderId: string) => void;
  onOpenInNewTab?: (flow: Flows) => void;
  onCreateInterface?: (flow: Flows, folderId: string) => void;
  onAddToLibrary?: (flow: Flows, folderId: string) => void;
  onDuplicate?: (flow: Flows, folderId: string) => void;
}

const AgentItemComponent: React.FC<AgentItemProps> = ({
  flow,
  folderId,
  flowFolderName,
  forceFluxbotIcon = false,
  isNested = false,
  spaceId = null,
  parentId,
  ancestorReservedType = null,
  onRename,
  onDelete,
  onShare,
  onOpenInNewTab,
  onCreateInterface,
  onAddToLibrary,
  onDuplicate,
}) => {
  const { activeFlowId, onFlowClick } = useAgentsSidebarContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hasOpened, setHasOpened] = useState(false);
  const isForm = isFormFlow(flow);
  const isFluxbot = forceFluxbotIcon || isFluxbotFlow(flow, flowFolderName);
  const kind = isFluxbot ? 'fluxbot' : isForm ? 'form' : 'agent';

  const typeIcon = isForm ? (
    <FileText size={16} />
  ) : isFluxbot ? (
    <ChatCircleText size={16} />
  ) : (
    <BoundingBox size={16} />
  );

  const draggable = useSidebarItemDraggable({
    itemId: flow.id,
    kind,
    parentId: parentId ?? flow.folderId ?? null,
    spaceId,
    label: flow.name,
    ancestorReservedType,
  });

  const handleMenuClick = (anchor: HTMLElement) => {
    setHasOpened(true);
    setAnchorEl(anchor);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <DraggableItemShell
      ref={draggable.setNodeRef}
      isDragging={draggable.isDragging}
      dropAbove={draggable.showDropAbove}
      dropBelow={draggable.showDropBelow}
    >
      <Item
        icon={
          <AutoTriggerBadge active={flow.isAutotrigger}>
            {typeIcon}
          </AutoTriggerBadge>
        }
        label={flow.name}
        isActive={activeFlowId === flow.id}
        isNested={isNested}
        onClick={() => onFlowClick(flow, folderId)}
        onMenuClick={handleMenuClick}
      />
      {(anchorEl || hasOpened) && (
        <ActionMenu
          anchorEl={anchorEl}
          onClose={handleClose}
          onRename={onRename ? () => onRename(flow, folderId) : undefined}
          onShare={onShare ? () => onShare(flow, folderId) : undefined}
          onOpenInNewTab={
            onOpenInNewTab ? () => onOpenInNewTab(flow) : undefined
          }
          onCreateInterface={
            !isForm && !isFluxbot && onCreateInterface
              ? () => onCreateInterface(flow, folderId)
              : undefined
          }
          onAddToLibrary={
            !isForm && !isFluxbot && onAddToLibrary
              ? () => onAddToLibrary(flow, folderId)
              : undefined
          }
          libraryLabel={flow.library ? 'Edit Library' : 'Add to Library'}
          onDuplicate={
            !isForm && onDuplicate
              ? () => onDuplicate(flow, folderId)
              : undefined
          }
          onDelete={onDelete ? () => onDelete(flow) : undefined}
        />
      )}
    </DraggableItemShell>
  );
};

export const AgentItem = React.memo(AgentItemComponent);
